// ============================================================
// `matchMedia` als Hook.
//
// Die Schwellen stehen HIER als Konstanten und in `styles/app.css` als
// Medienabfragen. Wer eine ändert, ändert sie an BEIDEN Stellen — sonst
// schaltet die Oberfläche bei 859 px in die Mobilfassung, während das CSS
// noch nach Schreibtisch aussieht.
//
// `useSyncExternalStore` statt `useState` + `useEffect`: damit stimmt der Wert
// schon beim ERSTEN Anstrich. Mit einem Effekt zeichnet die Karte auf dem
// Telefon zuerst die Seitenleiste und schaltet dann auf das Bodenblatt um —
// ein Sprung, den man sieht.
// ============================================================
import { useCallback, useSyncExternalStore } from "react";

/** Telefonfassung: Reiterleiste unten statt Reiter in der Kopfleiste (E20). */
export const MOBIL = "(max-width: 859px)";
/** Schmaler Schreibtisch (Seitenaufbau). Liest derzeit niemand. NICHT die Schwelle der
 *  Kopfleiste: unter welcher Breite dort nur Symbole bleiben, steht seit §34 gemessen in
 *  `styles/app.css` (1560 px) — eine Medienabfrage hier folgte ihr nicht mit. */
export const SCHMAL = "(max-width: 1180px)";
/** Grober Zeiger — Finger oder Stift. Entscheidet über Trefferflächen beim Zeichnen. */
export const GROB = "(pointer: coarse)";
/**
 * „Bewegung bitte sparsam". Das Federspiel der Avatare (E24) schaltet sich
 * daran ab; die CSS-Dauerschleifen erledigt base.css selbst.
 */
export const BEWEGUNG_AUS = "(prefers-reduced-motion: reduce)";

export function useMedienabfrage(query: string): boolean {
  const abonnieren = useCallback(
    (melde: () => void) => {
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => {};
      const ml = window.matchMedia(query);
      ml.addEventListener("change", melde);
      return () => ml.removeEventListener("change", melde);
    },
    [query],
  );

  const lesen = useCallback(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia(query).matches;
  }, [query]);

  // Der dritte Parameter ist der Serverwert. SSR gibt es hier nicht, aber der
  // Testaufsatz stellt `matchMedia` nach — ohne ihn wäre der erste Anstrich
  // in happy-dom ein Wurf.
  return useSyncExternalStore(abonnieren, lesen, () => false);
}

/** Kurzform für den häufigsten Fall. */
export function useMobil(): boolean {
  return useMedienabfrage(MOBIL);
}

/** true, wenn das Gerät um wenig Bewegung gebeten hat. */
export function useBewegungAus(): boolean {
  return useMedienabfrage(BEWEGUNG_AUS);
}
